import i18n from '@/i18n'
import { filterRulesMapped } from '@/tools/filterRules'
import { zoneService } from '@/services/zone.service'
import { driveUnitService } from '@/services/driveUnit.service'
import { mapActions, mapGetters } from 'vuex'
import { DialogType, SnackbarStyle } from '@/constants/constants'

export default {
    name: 'ZoneTableComponent',
    components: {
    },
    props: {
        enterprises: {
            type: Array,
            required: true
        }
    },
    data: () => ({
        // headers
        tableHeaders: [],
        // reglas de filtrado
        tableFilterRules: {},
        // datos
        tableData: [],
        // key de los dato de la tabla
        tableDataKey: '_id',
        // botones de cada fila
        rowActions: [
            {
                id: 1,
                name: i18n.t('modify'),
                color: 'blue',
                icon: 'fa-pen'
            },
            {
                id: 2,
                name: i18n.t('zone.driveUnit.add'),
                color: 'green',
                icon: 'fa-plus',
                disable (item) {
                    return item.isUm
                }
            },
            {
                id: 3,
                name: i18n.t('zone.driveUnit.edit'),
                color: 'green',
                icon: 'fa-pen',
                disable (item) {
                    return item.isUm ? !item.isUm : true
                }
            },
            {
                id: 4,
                name: i18n.t('delete'),
                color: 'red',
                icon: 'fa-trash-alt'
            }
        ],
        // botones del componente Tabla
        tableComponentButtons: [],
        // tamaño de página
        pageSize: undefined,
        // núm de página
        pageNumber: undefined,
        // total de elementos de la base de datos
        pageTotal: 0,
        // loading de la tabla
        loadingTable: true
    }),
    computed: {
        /**
         * getters del breadcrumb para el dialog
         */
        ...mapGetters('breadcrumbs', [
            'getBread'
        ])
    },
    created() {
    },
    beforeDestroy() {
    },
    mounted() {
        // inicialización de los datos del ABM
        this.initialize()
    },
    methods: {
        /**
         * actions del snackbar
         */
        ...mapActions('snackbar', [
            'showSnackbar',
            'closeSnackbar'
        ]),
        /**
         * actions del dialog
         */
        ...mapActions('dialog', [
            'openDialog',
            'closeDialog'
        ]),
        /**
         * actions del breadcrumbs
         */
        ...mapActions('breadcrumbs', [
            'pushBread',
            'pullBread',
            'cleanBread'
        ]),
        /**
         * Para inicializar el componente
         */
        async initialize () {
            // Botón de eliminación múltiple
            this.tableComponentButtons.push(
                {
                    id: 'masive-delete',
                    tooltip: i18n.t('multipleDelete'),
                    icon: 'delete_outline',
                    action: this.multipleDelete.bind(this),
                    selectedData: true
                }
            )
            
            // Botón para cambiar color de zonas seleccionadas
            this.tableComponentButtons.push(
                {
                    id: 'change-color',
                    tooltip: i18n.t('zone.changeColorTitle'),
                    icon: 'palette',
                    action: this.changeZoneColor.bind(this),
                    selectedData: true
                }
            )
            // total de líneas (para el paginador)
            this.pageTotal = await zoneService.getTotal(this.enterprises)

            // Si hay query params en la URL, los setea para incluirlos en la llamada al backend
            if (this.$route.query.pageSize && this.$route.query.pageNumber) {
                this.pageSize = Number(this.$route.query.pageSize)
                this.pageNumber = Number(this.$route.query.pageNumber)

                // Valida que los qery params sean correctos y completos
                if ((this.pageSize && this.pageSize <= 0) || (this.pageNumber && this.pageNumber <= 0) || (this.pageNumber && !this.pageSize) || (!this.pageNumber && this.pageSize)) {
                    this.showSnackbar({
                        visible: true,
                        text: this.$t('error.invalidQueryParams'),
                        timeout: 6000,
                        style: SnackbarStyle.ERROR
                    })
                    this.pageSize = this.pageTotal
                    this.pageNumber = 1
                }
            } else {
                this.pageSize = 50
                this.pageNumber = 1
            }

            // cargo la tabla
            this.getData()
        },
        /**
         * Obtiene los datos para la tabla del ABM
         * @param {*} update
         */
        async getData(update = false) {
            this.loadingTable = true
            if (update) {
                this.pageTotal = await zoneService.getTotal(this.enterprises)
                this.pageSize = 50
                this.pageNumber = 1
            }
            zoneService.findAllPaginated(this.enterprises, this.pageSize, this.pageNumber, !update).then(response => {
                this.tableData.cleanAndUpdate(response.data)
                if (!update) {
                    this.tableHeaders.cleanAndUpdate(response.headers)
                    this.setTableFilterRules()
                }
                this.loadingTable = false
            })
        },
        /**
         * Refrescar la data
         */
        refreshTable() {
            this.getData(true)
        },
        /**
         * Setea las reglas de filtrado
         */
        setTableFilterRules() {
            this.tableFilterRules = {}
            this.tableHeaders.forEach(header => {
                this.tableFilterRules[header.value] = filterRulesMapped[header.filterType]
            })
        },
        /**
         * Para eliminar una zona
         * @param {*} data
         */
        async delete(data) {
            await zoneService.deleteOne(data._id)
            await driveUnitService.deleteOne(data._id)
            this.getData(true)
        },
        /**
         * Para eliminación múltiple
         * @param {*} data
         */
        async multipleDelete(data) {
            this.openDialog({
                title: this.$t('delete'),
                text: this.$t('zone.multipleDeleteMessage'),
                type: DialogType.QUESTION,
                overlayOpacity: 0.5,
                actionButtons: true,
                yesAction: async () => {
                    const ids = data.map(e => e._id)
                    await zoneService.deleteMany(ids)
                    await driveUnitService.deleteMany(ids)
                    this.getData(true)
                    this.$emit('deleteZone')
                },
                noAction: () => {
                }
            })
        },
        /**
         * Para cambiar el color de las zonas seleccionadas
         * @param {*} data
         */
        async changeZoneColor(data) {
            this.$emit('openChangeColorDialog', data)
        },
        async rowButtonClicked(button, data) {
            switch (button.id) {
                case 1:
                    this.$emit('editZone', data)
                    break
                case 2:
                case 3:
                    this.$emit('openDriveUnitDialog', data)
                    break
                case 4:
                    this.openDialog({
                        title: this.$t('delete'),
                        text: this.$t('zone.deleteMessage', { name: data.name }),
                        type: DialogType.QUESTION,
                        overlayOpacity: 0.5,
                        actionButtons: true,
                        yesAction: () => {
                            this.delete(data)
                            this.$emit('deleteZone')
                        },
                        noAction: () => {
                        }
                    })
                    break
                default:
                    console.log('default')
            }
        },
        /**
         * Paginador externo
         * @param {*} pageSize
         * @param {*} pageNumber
         */
        externalPaginator(pageSize, pageNumber) {
            this.pageNumber = Number(pageNumber)
            this.pageSize = Number(pageSize)
        }
    },
    watch: {
        enterprises: {
            handler: function () {
                this.getData(true)
            }
        }
    }
}
